import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useStatisticsContext } from '../../../../../providers/StatisticsProvider';
import { useGetCustomersQuery } from '../../../../../slices/customersApiSlice';
import { useGetAssistantUsersQuery, useGetSaleUsersQuery } from '../../../../../slices/usersApiSlice';
import {
	clampCompareAnalysisFiltersForPeriod,
	filterOverviewComparesForLens,
	isCompareAnalysisFiltersUsable,
	isStatisticsOverviewComparePeriodEligible,
	mapComparesForLens,
	resolveCompareBaselineLabel,
	resolveCompareDeltaPrefix,
	resolveCompareDisplayKind,
} from '../../../filters/lib/statisticsCompareHelpers';
import { comparePeriodsEqual } from '../../../filters/lib/statisticsComparePresets';
import { isStatisticsManagerRole } from '../../../common/lib/statisticsAccess';
import { resolveBasetrendRow } from '../../../filters/lib/statisticsYoy';
import { resolveComparePeriodHeaderLabel, resolveStatisticsPeriodDisplayLabel } from '../../../filters/lib/statisticsPeriodQuickPickUtils';
import { getOrderPrimaryPhaseLabel, getOrderStatisticsPrimaryPhase, getStatisticsLensData, PROCESS_DISTRIBUTION_EXCLUDED_PHASES } from '../lib/orderPrimaryPhaseConfig';
import { aggregatePrimaryPhaseLensFromOrders } from '../lib/aggregatePrimaryPhaseLensFromOrders';
import { aggregatePrimaryPhaseOrdersByDimension } from '../lib/aggregatePrimaryPhaseOrdersByDimension';
import { ORDER_PRIMARY_PHASE_DIMENSIONS, resolveOrderDimensionKey } from '../lib/orderPrimaryPhaseDimension';
import { DEFAULT_ACTIVE_METRICS, resolveChartPrimaryMetric, togglePrimaryPhaseMetric } from '../lib/orderPrimaryPhaseMetric';
import { buildTrendChartBundle } from '../../trend/lib/buildTrendChartBundle';
import useTrendChartFilterSession from '../../trend/hooks/useTrendChartFilterSession';
import { TREND_CHART_FILTER_MODE } from '../../trend/lib/resolveTrendChartFilters';

const enrichCompareRow = (compare, selectedPhase) => ({
	...compare,
	compareKind: resolveCompareDisplayKind(compare.kind),
	baselineLabel: resolveCompareBaselineLabel(compare),
	deltaPrefix: resolveCompareDeltaPrefix(compare),
	headerRangeLabel: resolveComparePeriodHeaderLabel(compare),
	overviewRow: compare.overview ?? null,
	row: resolveBasetrendRow(compare.lensData, selectedPhase),
});

export const useOrderPrimaryPhaseStatsState = () => {
	const {
		primaryPhaseStats,
		statisticsFilters,
		statisticsFiltersDraft,
		patchTrendChartFilters,
		isStatisticsQueryLoading: isStatisticsQueryLoadingFromContext,
		isStatisticsQueryFetching,
		isComparesQueryLoading,
		isComparesQueryFetching,
		isOrdersListFetching: isOrdersListFetchingFromContext,
		ordersListQuery,
		setOrdersListQuery,
	} = useStatisticsContext();
	const { userInfo } = useSelector((state) => state.auth);
	const userRole = userInfo?.role;
	const isManager = isStatisticsManagerRole(userInfo);
	const { data: fetchedCustomers = [] } = useGetCustomersQuery(undefined, { skip: !isManager });
	const { data: fetchedSaleUsers = [] } = useGetSaleUsersQuery({ scope: 'order' }, { skip: !isManager });
	const { data: fetchedAssistantUsers = [] } = useGetAssistantUsersQuery({ scope: 'order' }, { skip: !isManager });
/** 流程口径固定 operational */
	const [activeMetrics, setActiveMetrics] = useState(DEFAULT_ACTIVE_METRICS);
	const activeMetric = useMemo(() => resolveChartPrimaryMetric(activeMetrics), [activeMetrics]);
	const [selectedPhase, setSelectedPhase] = useState(null);
	const [activeDimension, setActiveDimension] = useState(ORDER_PRIMARY_PHASE_DIMENSIONS.SALE_USER);
	const [selectedDimensionKey, setSelectedDimensionKey] = useState(null);
	const [tableViewMode, setTableViewMode] = useState('group');
	const ordersPanelRef = useRef(null);
	const isStatisticsQueryLoading = isStatisticsQueryLoadingFromContext && !primaryPhaseStats;
	const isOrdersListFetching = Boolean(isOrdersListFetchingFromContext);

	const allOrders = useMemo(() => primaryPhaseStats?.orders ?? [], [primaryPhaseStats?.orders]);
	const chartAggregates = primaryPhaseStats?.chartAggregates ?? null;
	const ordersPagination = primaryPhaseStats?.ordersPagination ?? { page: 1, limit: 50, total: 0 };
	const ordersTotal = ordersPagination.total ?? 0;

	const { chartFilters: trendChartFilters, compareUi: trendCompareUi } = useTrendChartFilterSession({
		mode: TREND_CHART_FILTER_MODE.PAGE_APPLIED,
		applied: statisticsFilters,
	});
	const trendChartBundle = useMemo(
		() =>
			buildTrendChartBundle({
				filters: trendChartFilters,
				primaryPhaseStats,
				selectedPhase,
				fetchedSaleUsers,
				fetchedAssistantUsers,
				fetchedCustomers,
				isStatisticsQueryLoading: isStatisticsQueryLoadingFromContext,
				isStatisticsQueryFetching,
			}),
		[
			fetchedAssistantUsers,
			fetchedCustomers,
			fetchedSaleUsers,
			isStatisticsQueryFetching,
			isStatisticsQueryLoadingFromContext,
			trendChartFilters,
			primaryPhaseStats,
			selectedPhase,
		],
	);
	const {
		compares: trendsForLens,
		highlightProvinceNames,
		highlightAnchorCityRows,
		trendYearMonthsMode,
		trendDualFullPeriod,
		trendGranularity,
		trendMonthIndices,
		trendPeriodIndices,
		trendPeriodYoy,
		trendAxisMode,
		trendDateField,
		trendYAxisMetric,
		trendYAxisMetrics,
		trendChartType,
		trendLineShape,
		trendBarOrientation,
		trendMetricSeriesTypes,
		trendYearSeriesColors,
		trendDimensionCountSeries,
		trendDimensionTotalPriceSeries,
		trendDimensionSeriesByMetric,
		trendDimensionFieldLabel,
		trendChartLoading,
		trendChartKey,
		trendScopeKey,
	} = trendChartBundle;
	const fullLensData = useMemo(() => getStatisticsLensData(primaryPhaseStats), [primaryPhaseStats]);
	const currentPeriodLabel = useMemo(() => {
		const startDate = statisticsFilters?.startDate ?? primaryPhaseStats?.currentPeriod?.startDate;
		const endDate = statisticsFilters?.endDate ?? primaryPhaseStats?.currentPeriod?.endDate;
		const displayLabel = resolveStatisticsPeriodDisplayLabel(startDate, endDate);
		if (displayLabel) return displayLabel;
		return primaryPhaseStats?.currentPeriod?.label ?? '';
	}, [
		statisticsFilters?.endDate,
		statisticsFilters?.startDate,
		primaryPhaseStats?.currentPeriod?.endDate,
		primaryPhaseStats?.currentPeriod?.label,
		primaryPhaseStats?.currentPeriod?.startDate,
	]);

	const comparesForLens = useMemo(() => {
		const mapped = mapComparesForLens(primaryPhaseStats?.compares);
		const filterPeriods = statisticsFilters?.customComparePeriods ?? [];
		if (!filterPeriods.length) return mapped;

		return mapped.map((item) => {
			if (item.kind !== 'custom') return item;
			const matched = filterPeriods.find((period) =>
				comparePeriodsEqual(
					{ startDate: period?.startDate, endDate: period?.endDate },
					{ startDate: item.periodStartDate, endDate: item.periodEndDate },
				),
			);
			const filterLabel = matched?.label?.trim();
			return filterLabel ? { ...item, title: filterLabel } : item;
		});
	}, [statisticsFilters?.customComparePeriods, primaryPhaseStats?.compares]);

	const overviewComparesForLens = useMemo(() => filterOverviewComparesForLens(comparesForLens), [comparesForLens]);

	const overviewCompareRows = useMemo(
		() => overviewComparesForLens.map((item) => enrichCompareRow(item, null)),
		[overviewComparesForLens],
	);

	const momCompare = useMemo(() => overviewComparesForLens.find((item) => item.kind === 'mom') ?? null, [overviewComparesForLens]);

	const hasCustomOrderRange = Boolean(statisticsFilters?.startDate && statisticsFilters?.endDate);
	const overviewCompareEligible = useMemo(
		() =>
			isStatisticsOverviewComparePeriodEligible({
				startDate: statisticsFilters?.startDate,
				endDate: statisticsFilters?.endDate,
				trendAnchor: statisticsFilters?.trendAnchor,
				datePeriods: statisticsFilters?.datePeriods,
			}),
		[
			statisticsFilters?.datePeriods,
			statisticsFilters?.endDate,
			statisticsFilters?.trendAnchor,
			statisticsFilters?.startDate,
		],
	);
	const { compareAnalysisDisabled: comparePeriodDisabled, compareAnalysisDisabledHint: comparePeriodDisabledHint } = trendCompareUi;

	const showMomHint = useMemo(() => {
		if (momCompare) return false;
		return !hasCustomOrderRange;
	}, [hasCustomOrderRange, momCompare]);

	const overviewCompareLoading = useMemo(() => {
		if (!overviewCompareEligible) return false;
		if (isStatisticsQueryLoading) return true;
		if (isComparesQueryLoading || isComparesQueryFetching) return true;
		return false;
	}, [isComparesQueryFetching, isComparesQueryLoading, isStatisticsQueryLoading, overviewCompareEligible]);

	const phaseScopedOrders = useMemo(() => {
		if (!selectedPhase) return allOrders;
		return allOrders.filter((order) => getOrderStatisticsPrimaryPhase(order) === selectedPhase);
	}, [allOrders, selectedPhase]);

	const scopedOrders = useMemo(() => {
		if (ordersPagination.total != null && primaryPhaseStats?.orders) {
			return primaryPhaseStats.orders;
		}
		if (!selectedDimensionKey) return phaseScopedOrders;
		return phaseScopedOrders.filter((order) => resolveOrderDimensionKey(order, activeDimension) === selectedDimensionKey);
	}, [activeDimension, ordersPagination.total, phaseScopedOrders, primaryPhaseStats?.orders, selectedDimensionKey]);

	const dimensionRows = useMemo(() => {
		const aggregateRows = chartAggregates?.dimensions?.[activeDimension];
		if (!selectedPhase && Array.isArray(aggregateRows) && aggregateRows.length > 0) {
			return aggregateRows;
		}
		if (allOrders.length > 0) {
			return aggregatePrimaryPhaseOrdersByDimension(phaseScopedOrders, activeDimension, {
				filters: statisticsFilters,
			});
		}
		return aggregateRows ?? [];
	}, [activeDimension, allOrders.length, chartAggregates?.dimensions, phaseScopedOrders, selectedPhase, statisticsFilters]);

	const selectedDimensionLabel = useMemo(() => {
		if (!selectedDimensionKey) return '';
		return dimensionRows.find((row) => row.key === selectedDimensionKey)?.label ?? '';
	}, [dimensionRows, selectedDimensionKey]);

	const lensData = useMemo(() => {
		if (!selectedPhase) return fullLensData;
		if (allOrders.length > 0) {
			return aggregatePrimaryPhaseLensFromOrders(scopedOrders, statisticsFilters);
		}
		const phaseRow = fullLensData?.phases?.find((row) => row.phase === selectedPhase);
		if (!phaseRow) return fullLensData;
		return {
			overview: {
				count: phaseRow.count,
				totalPrice: phaseRow.totalPrice,
				receivableTotal: phaseRow.receivableTotal,
				quasiReceivableTotal: phaseRow.quasiReceivableTotal,
				pendingPrepaymentTotal: phaseRow.pendingPrepaymentTotal,
				paidPrepaymentTotal: phaseRow.paidPrepaymentTotal,
			},
			phases: [phaseRow],
		};
	}, [allOrders.length, fullLensData, scopedOrders, selectedPhase, statisticsFilters]);

	const selectedPhaseLabel = selectedPhase ? getOrderPrimaryPhaseLabel(selectedPhase) : '';
	const chartAnimationKey = `${activeMetrics.join(',')}-${selectedPhase ?? 'all'}-${activeDimension}-${selectedDimensionKey ?? 'all'}-${ordersTotal}-${trendChartKey}-${trendScopeKey}`;

	useEffect(() => {
		setOrdersListQuery((prev) => ({
			...prev,
			page: 1,
			phase: selectedPhase,
			dimension: selectedDimensionKey ? activeDimension : null,
			dimensionKey: selectedDimensionKey,
		}));
	}, [activeDimension, selectedDimensionKey, selectedPhase, setOrdersListQuery]);

	const handleOrdersListPageChange = useCallback(
		(nextPage) => {
			setOrdersListQuery((prev) => ({
				...prev,
				page: Math.max(1, nextPage),
			}));
		},
		[setOrdersListQuery],
	);

	const handleOrdersListStatusFilterChange = useCallback(
		({ paymentStatuses, deliveryStatuses, inProgress, fulfillmentComplete, pendingReview, verified } = {}) => {
			setOrdersListQuery((prev) => ({
				...prev,
				page: 1,
				paymentStatuses: Array.isArray(paymentStatuses) ? paymentStatuses : [],
				deliveryStatuses: Array.isArray(deliveryStatuses) ? deliveryStatuses : [],
				inProgress: Boolean(inProgress),
				fulfillmentComplete: Boolean(fulfillmentComplete),
				pendingReview: Boolean(pendingReview),
				verified: Boolean(verified),
			}));
		},
		[setOrdersListQuery],
	);

	useEffect(() => {
		/** 选期不规范时清掉页级残留对比段（旧书签 / 历史 URL），避免总览查询带着无效 customComparePeriods */
		if (isCompareAnalysisFiltersUsable(statisticsFiltersDraft)) return;

		const hasStaleSegments =
			(statisticsFiltersDraft?.customComparePeriods?.length ?? 0) > 0 ||
			(statisticsFiltersDraft?.trendMonthlyYears?.length ?? 0) > 0;
		if (!hasStaleSegments) return;

		const clamped = clampCompareAnalysisFiltersForPeriod(statisticsFiltersDraft);
		patchTrendChartFilters({
			customComparePeriods: clamped.customComparePeriods,
			trendMode: clamped.trendMode,
			trendMonthlyYears: clamped.trendMonthlyYears,
			trendAnchor: clamped.trendAnchor,
		});
	}, [patchTrendChartFilters, statisticsFiltersDraft]);

	useEffect(() => {
		if (selectedPhase && PROCESS_DISTRIBUTION_EXCLUDED_PHASES.has(selectedPhase)) {
			setSelectedPhase(null);
		}
	}, [selectedPhase]);

	useEffect(() => {
		setSelectedDimensionKey(null);
	}, [activeDimension, selectedPhase]);

	const handleSelectPhase = useCallback((phase) => {
		if (PROCESS_DISTRIBUTION_EXCLUDED_PHASES.has(phase)) return;
		setSelectedPhase((prev) => (prev === phase ? null : phase));
	}, []);

	const handleSelectOverview = useCallback(() => {
		setSelectedPhase(null);
	}, []);

	const clearSelectedPhase = useCallback(() => setSelectedPhase(null), []);

	const handleSelectDimension = useCallback((key) => {
		setSelectedDimensionKey((prev) => (prev === key ? null : key ?? null));
		if (key) setTableViewMode('flat');
	}, []);

	const clearSelectedDimension = useCallback(() => setSelectedDimensionKey(null), []);

	const toggleActiveMetric = useCallback((metric) => {
		setActiveMetrics((prev) => togglePrimaryPhaseMetric(prev, metric));
	}, []);

	const clearTableFilters = useCallback(() => {
		setSelectedPhase(null);
		setSelectedDimensionKey(null);
	}, []);

	return {
		activeMetrics,
		toggleActiveMetric,
		activeMetric,
		selectedPhase,
		ordersPanelRef,
		isStatisticsQueryLoading,
		isOrdersListFetching,
		trendChartLoading,
		lensData,
		fullLensData,
		overviewCompareRows,
		comparesForLens,
		trendsForLens,
		trendYearMonthsMode,
		trendDualFullPeriod,
		trendGranularity,
		trendMonthIndices,
		trendPeriodIndices,
		trendPeriodYoy,
		trendAxisMode,
		trendDateField,
		trendYAxisMetric,
		trendYAxisMetrics,
		trendChartType,
		trendLineShape,
		trendBarOrientation,
		trendMetricSeriesTypes,
		trendYearSeriesColors,
		trendDimensionCountSeries,
		trendDimensionTotalPriceSeries,
		trendDimensionSeriesByMetric,
		trendDimensionFieldLabel,
		trendChartKey,
		trendScopeKey,
		showMomHint,
		comparePeriodDisabled,
		comparePeriodDisabledHint,
		overviewCompareLoading,
		currentPeriodLabel,
		orders: scopedOrders,
		highlightProvinceNames,
		highlightAnchorCityRows,
		chartAggregates,
		phaseScopedOrders,
		allOrders,
		tableOrders: scopedOrders,
		ordersPagination,
		ordersTotal,
		handleOrdersListPageChange,
		handleOrdersListStatusFilterChange,
		ordersListPaymentFilter: ordersListQuery?.paymentStatuses ?? [],
		ordersListDeliveryFilter: ordersListQuery?.deliveryStatuses ?? [],
		ordersListFulfillmentFilter: [
			...(ordersListQuery?.inProgress ? ['inProgress'] : []),
			...(ordersListQuery?.fulfillmentComplete ? ['fulfillmentComplete'] : []),
			...(ordersListQuery?.pendingReview ? ['pendingReview'] : []),
			...(ordersListQuery?.verified ? ['verified'] : []),
		],
		dimensionRows,
		activeDimension,
		setActiveDimension,
		selectedDimensionKey,
		selectedDimensionLabel,
		tableViewMode,
		setTableViewMode,
		selectedPhaseLabel,
		chartAnimationKey,
		handleSelectPhase,
		handleSelectOverview,
		handleSelectDimension,
		clearSelectedPhase,
		clearSelectedDimension,
		clearTableFilters,
		primaryPhaseStats,
		fetchedSaleUsers,
		fetchedAssistantUsers,
		fetchedCustomers,
	};
};
